import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Typography, Skeleton } from "investira.react.components";
import services from "../../../../services";
import validators from "investira.sdk/lib/utils/validators";
import BenchmarksIndicesRentabilidade from "./BenchmarksIndicesRentabilidade";
import BenchmarksRentabilidade from "./BenchmarksRentabilidade";
import { dates, objects } from "investira.sdk";

const Benchmarks = memo(() => {
  const [isLoading, setIsLoading] = useState(false);
  const [benchmarks, setBenchmarks] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    const xBenchmarks = {};
    services.benchmarks.list(
      {},
      (rRes) => {
        if (!rRes.data || rRes.data.length === 0) {
          setIsLoading(false);
          return;
        }
        const xIndicadoreIds = rRes.data.map((xIndicador) => {
          return xIndicador.indicador_id;
        });
        services.indicadores.cotacao(
          {
            // indicador_id: "IGPM,IBOV",
            indicador_id: xIndicadoreIds.join(","),
            data_inicio: dates.addYears(dates.toSqlDate(new Date()), -5),
            cotacao_tipo_id: "1,4",
          },
          (rRes) => {
            const xBenchmarksResult = rRes.data.reduce((xAcc, xValue) => {
              if (!xAcc[xValue.indicador_id]) {
                xAcc[xValue.indicador_id] = {
                  indicador_seuid: xValue.indicador_seuid,
                  data: [],
                };
              }
              xAcc[xValue.indicador_id].data.push(xValue);
              return xAcc;
            }, xBenchmarks);
            const xBenchmarksResolved = Object.values(xBenchmarksResult).map(
              (xValue) => {
                return {
                  current: xValue.indicador_seuid,
                  historico: createHistoric(xValue.data, xValue.data[0].data),
                };
              },
            );
            setBenchmarks(Object.values(xBenchmarksResolved));
          },
          (rErr) => {
            console.log(rErr);
          },
          () => {
            setIsLoading(false);
          },
        );
      },
      (rErr) => {
        console.log(rErr);
      },
    );
  }, []);

  const createHistoric = useCallback((pData, pDataCorte) => {
    let xData = pData;

    if (!validators.isNull(pDataCorte)) {
      xData = filtrarPorData(xData, pDataCorte);
    }

    const xHistorico = {
      ytd: adicionarRentabilidade(
        filtrarPorData(xData, new Date(new Date().getFullYear(), 0, 1)),
      ),
      umMes: adicionarRentabilidade(filtrarPorPeriodo(xData, -1)),
      seisMeses: adicionarRentabilidade(filtrarPorPeriodo(xData, -6)),
      umAno: adicionarRentabilidade(filtrarPorPeriodo(xData, -12)),
      doisAnos: adicionarRentabilidade(filtrarPorPeriodo(xData, -24)),
      tresAnos: adicionarRentabilidade(filtrarPorPeriodo(xData, -36)),
      quatroAnos: adicionarRentabilidade(filtrarPorPeriodo(xData, -48)),
      cincoAnos: adicionarRentabilidade(filtrarPorPeriodo(xData, -60)),
      full: adicionarRentabilidade(xData),
    };

    return xHistorico;
  }, []);

  const filtrarPorData = (pDados, pDataEspecifica) => {
    const xData = new Date(pDataEspecifica);
    return pDados.filter((xItem) => new Date(xItem.data) >= xData);
  };

  const filtrarPorPeriodo = (pDados, pMeses, pDataRef = dates.toDate()) => {
    const xDataLimite = dates.toSqlDate(dates.addMonths(pDataRef, pMeses));

    return pDados.filter(
      (xItem) => dates.toSqlDate(new Date(xItem.data)) >= xDataLimite,
    );
  };

  const adicionarRentabilidade = (pData) => {
    const xData = objects.deepCopy([...pData]);
    if (xData.length < 2) {
      return pData;
    }

    let xRentabilidade = 1;

    // O primeiro elemento não tem rentabilidade calculável, então definimos como n0
    xData[0].rentabilidade = 0;

    for (let i = 1; i < xData.length; i++) {
      xRentabilidade = xRentabilidade * xData[i].variacao_diaria;
      // Adiciona o novo atributo 'rentabilidade' ao objeto existente
      xData[i].rentabilidade = (xRentabilidade - 1) * 100;
    }

    return xData;
  };

  return (
    <Stack
      gap={2}
      component="section"
      sx={{ gap: 3 }}
      flexGrow={1}
      direction="column"
      justifyContent="space-between"
    >
      <Stack gap={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="h6" color="textPrimary" component="h3">
            Meus Benchmarks
          </Typography>
        </Stack>
      </Stack>
      {isLoading ? (
        <>
          <Skeleton
            variant="rectangular"
            width="100%"
            height="500px"
            sx={{ borderRadius: 2 }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height="200px"
            sx={{ borderRadius: 2 }}
          />
        </>
      ) : (
        <>
          <BenchmarksRentabilidade benchmarks={benchmarks} />
          <BenchmarksIndicesRentabilidade benchmarks={benchmarks} />
        </>
      )}
    </Stack>
  );
});

export default Benchmarks;
